import { createFileRoute, Link } from "@tanstack/react-router";
import { PageShell } from "@/components/PageShell";
import { SectionHeader } from "@/components/SectionHeader";
import { Reveal } from "@/components/Reveal";
import { ArrowRight, ShieldCheck, Radar, Camera, Zap, Building2 } from "lucide-react";
import solPerimeter from "@/assets/sol-perimeter.jpg";
import { ogUrl, sectionImageMeta } from "@/lib/ogSections";
import { hreflangLinks } from "@/lib/hreflang";
import { pageSchemaScript, PERSON_ID } from "@/seo/schema";

const SITE_ORIGIN = "https://arifkhanglobal.com";
const PATH = "/security-solutions";
const URL = `${SITE_ORIGIN}${PATH}`;
const TITLE = "Perimeter Security Cost India | Electric Fence Delhi NCR";
const DESCRIPTION =
  "Perimeter security system cost in India and electric fence installation Delhi NCR: compare detection, CCTV, safety, integration and maintenance.";
const OG_IMAGE = ogUrl(solPerimeter);

const PILLARS = [
  {
    Icon: Radar,
    title: "Perimeter intrusion detection",
    desc: "Fibre-optic fence sensors, microwave barriers, buried cable and radar, layered so a single technology never carries the whole boundary. Detection zones are drawn against terrain, vegetation and approach routes, then tuned for a nuisance-alarm rate operators will actually keep armed.",
  },
  {
    Icon: Zap,
    title: "Electric fencing",
    desc: "Energiser sizing, zoning and earthing designed to statutory clearances, with tamper and cut-line monitoring integrated into the same head-end as detection and video. Specified so the fence is a deterrent and a sensor — not an isolated deterrent nobody monitors.",
  },
  {
    Icon: Camera,
    title: "CCTV & video analytics",
    desc: "Coverage modelled from pixel-density targets rather than camera counts: detect, recognise and identify zones set per risk. Thermal along the fence line, colour at the gates, analytics tuned for the actual scene, and retention sized to your incident-review window.",
  },
  {
    Icon: Building2,
    title: "Command centre integration",
    desc: "Perimeter, fencing, CCTV, access control and PA brought into one operator workflow with defined alarm handling, escalation and audit trails. The console layout, SOPs and video-wall logic are designed before any display is bought.",
  },
];

const CASE_STUDIES = [
  {
    name: "INS Rajali Naval Air Station",
    scope: "15 km perimeter security program — layered intrusion detection, thermal surveillance and integrated command-and-control across an operational naval air station.",
    outcome:
      "A continuous, monitored boundary with zone-level alarm localisation, so response teams reach the exact segment instead of patrolling the whole line. Nuisance alarms were driven down to a level operators sustain in daily use.",
  },
  {
    name: "Industrial estate, North India",
    scope: "Electric fencing with cut-line and tamper monitoring across a multi-gate industrial boundary, integrated with ANPR and CCTV at every vehicle entry.",
    outcome:
      "Fence events, gate cycles and camera streams share a single timeline, cutting incident-review time from hours of tape scrubbing to a minute of correlated playback.",
  },
  {
    name: "Private estate, Delhi NCR",
    scope: "Discreet perimeter detection and CCTV for a large private estate where visible hardware was unacceptable to the owner and the landscape design.",
    outcome:
      "Full boundary coverage delivered with concealed sensors and low-profile thermal, verified by night-time walk-tests before handover and backed by an AMC and operator brief.",
  },
];

const MISTAKES = [
  "One perimeter technology used end-to-end, regardless of terrain",
  "Electric fencing installed as a deterrent with no monitoring or integration",
  "CCTV specified by camera count instead of pixel density per zone",
  "No nuisance-alarm tuning — the system is disarmed within weeks",
  "Retention sized to storage budget rather than incident-review reality",
];

const FAQS = [
  {
    q: "How is perimeter security different from just installing CCTV?",
    a: "CCTV records; perimeter detection alerts. A camera without detection depends on someone watching it at the right moment. A layered perimeter raises a zone-level alarm and slews the right camera to it, so video becomes verification rather than surveillance.",
  },
  {
    q: "Is electric fencing legal and safe for commercial sites in India?",
    a: "Yes, when energisers, voltages, clearances and signage follow the applicable standards and local regulations. The design must document earthing, zoning and access-gate isolation — that documentation is part of what we deliver, not an afterthought for the installer.",
  },
  {
    q: "How much perimeter can one command centre realistically monitor?",
    a: "It depends far more on the alarm rate than on the kilometres. A well-tuned system with zone localisation and camera slewing lets a small team hold many kilometres; a noisy one overwhelms operators inside a single kilometre.",
  },
  {
    q: "Do you supply the hardware?",
    a: "No. The design is vendor-agnostic: risk assessment, layered detection strategy, CCTV coverage drawings, command-centre workflow and a brand-neutral BOQ. Your integrator implements to that brief, and we review what gets built.",
  },
];

const RELATED = [
  { to: "/solutions/perimeter-protection", label: "Perimeter Protection solution" },
  { to: "/solutions/cctv-command-centre", label: "CCTV & Command Centre" },
  { to: "/solutions/security-infrastructure", label: "Security Infrastructure" },
  { to: "/locations/security-consultant-delhi-ncr", label: "Security Consultant in Delhi NCR" },
  { to: "/ask-arif/how-does-perimeter-security-work", label: "How does perimeter security work?" },
  { to: "/ask-arif/mistakes-in-perimeter-security", label: "Mistakes in perimeter security" },
] as const;

export const Route = createFileRoute("/security-solutions")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: URL },
      { property: "og:type", content: "article" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      ...sectionImageMeta(OG_IMAGE, "Perimeter security fence line with thermal surveillance camera"),
    ],
    links: [{ rel: "canonical", href: URL }, ...hreflangLinks(PATH)],
    scripts: [
      pageSchemaScript(
        {
          path: PATH,
          type: "WebPage",
          name: TITLE,
          description: DESCRIPTION,
          breadcrumb: [{ name: "Security Solutions", url: PATH }],
          extra: { about: { "@id": PERSON_ID } },
        },
        [
          {
            "@type": "Service",
            "@id": `${URL}#service`,
            name: "Perimeter Security, Electric Fencing & CCTV Solutions",
            serviceType: "Perimeter Intrusion Detection, Electric Fencing and CCTV Consultancy",
            provider: { "@id": PERSON_ID },
            areaServed: { "@type": "Country", name: "India" },
            url: URL,
            description: DESCRIPTION,
          },
          {
            "@type": "FAQPage",
            "@id": `${URL}#faq`,
            inLanguage: "en-IN",
            isPartOf: { "@id": `${URL}#webpage` },
            mainEntity: FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ],
      ),
    ],
  }),
});

function Page() {
  return (
    <PageShell>
      <section className="relative pt-40 pb-24 border-b border-border bg-surface overflow-hidden">
        <div className="absolute inset-0 grain pointer-events-none" />
        <div
          aria-hidden
          className="absolute inset-0 opacity-50"
          style={{
            backgroundImage: `url(${solPerimeter})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "blur(40px) saturate(1.1)",
            transform: "scale(1.15)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-surface via-surface/85 to-surface/60" />
        <div className="container-page relative">
          <div className="flex items-center gap-2 text-ink-soft">
            <ShieldCheck className="h-4 w-4" />
            <span className="eyebrow">Security Solutions</span>
          </div>
          <h1 className="mt-6 text-balance text-5xl md:text-7xl font-semibold tracking-tight leading-[0.95] max-w-4xl">
            Perimeter security, electric fencing and CCTV
          </h1>
          <p className="mt-6 max-w-2xl text-base md:text-xl text-ink-soft leading-relaxed">
            Independent design for boundaries that have to hold — 23+ years and
            500+ km of perimeter secured across naval, industrial, government and
            private estates in India. Detection, fencing, video and command-centre
            workflow engineered as one system, before any vendor pitches.
          </p>
        </div>
      </section>

      <section className="py-24">
        <div className="container-page">
          <SectionHeader
            eyebrow="Capabilities"
            title="Four layers, designed as one boundary."
            description="Detection, deterrence, verification and response — specified together so each layer covers the failure modes of the others."
          />
          <div className="mt-14 grid sm:grid-cols-2 gap-5">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.05}>
                <div className="h-full rounded-2xl border border-border bg-surface p-6">
                  <p.Icon className="h-5 w-5 text-primary" strokeWidth={1.5} />
                  <h2 className="mt-6 text-lg font-semibold tracking-tight">{p.title}</h2>
                  <p className="mt-3 text-sm text-ink-soft leading-relaxed">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-surface border-y border-border">
        <div className="container-page">
          <SectionHeader
            eyebrow="Case studies"
            title="Boundaries delivered, and what changed."
            description="Selected perimeter, fencing and CCTV programs — scope and the operational outcome that followed."
          />
          <div className="mt-14 grid lg:grid-cols-3 gap-5">
            {CASE_STUDIES.map((c, i) => (
              <Reveal key={c.name} delay={i * 0.05}>
                <article className="h-full rounded-2xl border border-border bg-background p-6">
                  <h3 className="text-lg font-semibold tracking-tight">{c.name}</h3>
                  <p className="mt-4 text-xs font-mono uppercase tracking-wider text-ink-soft">Scope</p>
                  <p className="mt-2 text-sm text-ink-soft leading-relaxed">{c.scope}</p>
                  <p className="mt-4 text-xs font-mono uppercase tracking-wider text-ink-soft">Outcome</p>
                  <p className="mt-2 text-sm text-ink-soft leading-relaxed">{c.outcome}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="container-page grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeader eyebrow="Common mistakes" title="Why perimeter systems get switched off." />
          </div>
          <div className="lg:col-span-7 grid gap-4">
            {MISTAKES.map((m, i) => (
              <Reveal key={m} delay={i * 0.04}>
                <div className="flex items-start gap-4 rounded-xl border border-border bg-surface p-5">
                  <div className="font-mono text-xs text-ink-soft w-8 pt-0.5">{String(i + 1).padStart(2, "0")}</div>
                  <div className="text-sm text-foreground leading-relaxed">{m}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-surface border-y border-border">
        <div className="container-page grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeader eyebrow="Related" title="Where this connects." />
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {RELATED.map((r) => (
              <Link
                key={r.to}
                to={r.to as "/"}
                className="tap-target group flex items-center justify-between rounded-xl border border-border bg-background p-5 hover:border-border-strong hover:shadow-card transition-all"
              >
                <span className="text-sm font-medium">{r.label}</span>
                <ArrowRight className="h-4 w-4 text-ink-soft group-hover:text-primary transition-colors" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="container-page grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeader eyebrow="FAQ" title="Questions we hear on perimeter projects." />
          </div>
          <div className="lg:col-span-7 grid gap-4">
            {FAQS.map((f, i) => (
              <Reveal key={f.q} delay={i * 0.05}>
                <details className="group rounded-xl border border-border bg-surface p-5 open:border-border-strong open:shadow-card transition-all">
                  <summary className="tap-target flex cursor-pointer list-none items-start justify-between gap-4 font-medium">
                    <span>{f.q}</span>
                    <ShieldCheck className="h-4 w-4 mt-1 text-ink-soft group-open:text-primary transition-colors" />
                  </summary>
                  <p className="mt-3 text-sm text-ink-soft leading-relaxed">{f.a}</p>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 border-t border-border bg-surface">
        <div className="container-page flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <div>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight leading-tight max-w-2xl text-balance">
              Planning a perimeter, fencing or CCTV program?
            </h2>
            <p className="mt-3 text-ink-soft">
              Share the site boundary, risk profile and current stage. An independent review before procurement.
            </p>
          </div>
          <Link
            to="/contact"
            className="tap-target inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-medium text-primary-foreground hover:shadow-elevated transition-shadow"
          >
            Let's Talk <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </PageShell>
  );
}
